// Implements: F-MCK-017, F-UIX-009, F-UIX-007
/**
 * The trash as a table (the mockup's trash view): the name, when it was sent here, when it is purged,
 * and the two actions. Pure presentation: both actions are plain callbacks, and the confirmation
 * that guards the irreversible one belongs to the container, which owns the question.
 */

import type { ReactElement } from "react";

import { useTranslation } from "../../../shared/i18n";
import type { TrashEntry } from "../../../types/api";
import { dayText } from "../../../shared/ui/dayText";
import { Button } from "../../../shared/ui/atoms/Button";
import { Icon } from "../../../shared/ui/atoms/Icon";
import styles from "./TrashTable.module.css";

export interface TrashTableProps {
  readonly entries: readonly TrashEntry[];
  readonly disabled?: boolean | undefined;
  readonly onRestore: (entry: TrashEntry) => void;
  readonly onDeleteForever: (entry: TrashEntry) => void;
}

export function TrashTable({ entries, disabled = false, onRestore, onDeleteForever }: TrashTableProps): ReactElement {
  const { t } = useTranslation();
  return (
    <div className={styles.wrap}>
      <table className={styles.table} aria-label={t("gallery.trash.table.label")}>
        <thead>
          <tr>
            <th scope="col">{t("gallery.trash.col.name")}</th>
            <th scope="col">{t("gallery.trash.col.trashed")}</th>
            <th scope="col">{t("gallery.trash.col.purge")}</th>
            <th scope="col" className={styles.actionsHead}>
              <span className={styles.srOnly}>{t("gallery.col.actions")}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {entries.map((entry) => (
            <tr key={entry.id}>
              <td className={styles.name}>{entry.name}</td>
              <td className={styles.date}>
                <time dateTime={entry.trashed_at}>{dayText(entry.trashed_at)}</time>
              </td>
              <td className={styles.date}>
                {entry.purge_after === undefined || entry.purge_after === null ? null : (
                  <time dateTime={entry.purge_after}>{dayText(entry.purge_after)}</time>
                )}
              </td>
              <td className={styles.actions}>
                <Button
                  variant="secondary"
                  disabled={disabled}
                  onClick={() => {
                    onRestore(entry);
                  }}
                >
                  <Icon name="restore" size="sm" />
                  {t("gallery.trash.restore")}
                </Button>{" "}
                <Button
                  variant="ghost"
                  disabled={disabled}
                  onClick={() => {
                    onDeleteForever(entry);
                  }}
                >
                  <span className={styles.danger}>{t("gallery.trash.deleteForever")}</span>
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
